// client/src/coms/organisation/LibraryView.js
// A server's "Files & resources" area. "Files" is one file manager for everything the server has: Voxemera's
// own small files and Google Drive files linked by members, in the same folders with the same search, sorting,
// previews and menus. Chat attachments (indexed in place, permission-filtered), the trash and other connected
// libraries are listed beside it. The Resources tab holds curated resource collections.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGetJson, apiPostJson, apiPatchJson, apiPut, apiDelete, uploadForm, directUpload, serverPath } from './orgApi';
import { formatBytes, formatDate, ProviderBadge, kindIcon, notify, Dialog, useConfirm, usePrompt, Spinner, EmptyState, ErrorNote, openTarget, PROVIDERS } from './orgShared';
import { saveBookmark } from './bookmarkActions';
import ExternalBrowser, { LinkVisibilityDialog, DriveSharingDialog } from './ExternalBrowser';
import ConnectStorageDialog from './ConnectStorageDialog';
import { AccountCard, connectWithFeedback, disconnectAccount, pickDriveFiles, openWhenReady } from './storageAccounts';
import ResourcesPanel from './ResourcesPanel';
import { FilePreview } from './FilePreview';
import { ItemRow, Breadcrumbs, statusLabel } from './libraryParts';

const SORTS = [
  ['name', 'Name'],
  ['updated', 'Last changed'],
  ['created', 'Uploaded'],
  ['size', 'Size'],
  ['type', 'Type'],
];
const TYPES = [
  ['', 'All types'],
  ['image', 'Images'],
  ['video', 'Video'],
  ['audio', 'Audio'],
  ['pdf', 'PDF'],
  ['document', 'Documents'],
  ['text', 'Text'],
  ['archive', 'Archives'],
];

function readViewMode() {
  try {
    return localStorage.getItem('vox:library:view') === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

export default function LibraryView({ server, initial }) {
  const serverKey = server && server.id != null ? String(server.id) : '';
  const base = serverPath(serverKey);
  const [tab, setTab] = useState(initial && initial.tab === 'resources' ? 'resources' : 'files');
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState([]);
  const [sel, setSel] = useState(() => (initial && initial.source ? initial.source : { kind: 'internal' }));
  const [err, setErr] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [filesSettings, setFilesSettings] = useState(null);

  const loadSettings = useCallback(() => (serverKey ? apiGetJson(`${base}/files/settings`).then(setFilesSettings, () => setFilesSettings(null)) : Promise.resolve()), [base, serverKey]);
  const load = useCallback(async () => {
    if (!serverKey) return;
    setErr(null);
    try {
      const [o, s] = await Promise.all([apiGetJson(`${base}/library`), apiGetJson(`${base}/library/sources`).catch(() => ({ sources: [] })), loadSettings()]);
      setOverview(o);
      setSources(s.sources || []);
    } catch (e) {
      setErr(e);
    }
  }, [base, serverKey, loadSettings]);

  useEffect(() => {
    load();
  }, [load]);

  // Deep links from search, bookmarks and resources.
  useEffect(() => {
    const apply = (d) => {
      if (!d || d.area !== 'library') return;
      if (d.tab) setTab(d.tab === 'resources' ? 'resources' : 'files');
      if (d.source) setSel(d.source);
    };
    apply(initial);
    const on = (e) => apply(e.detail);
    window.addEventListener('vox-open-org', on);
    return () => window.removeEventListener('vox-open-org', on);
  }, [initial]);

  if (!serverKey) return <EmptyState title="Choose a server">Open a server to see its files and resources.</EmptyState>;
  const perms = (overview && overview.permissions) || {};

  // The server's Drive links library is part of "Files"; other connected libraries keep their own entries.
  const driveLibId = filesSettings && filesSettings.drive_library_id;
  const otherSources = sources.filter((s) => s.id !== driveLibId);
  const navItems = [
    {
      key: 'internal',
      label: 'Files',
      icon: '🗂️',
      sub: filesSettings && filesSettings.effective_destination === 'gdrive' ? 'Google Drive and Voxemera' : overview ? `Voxemera: ${formatBytes(overview.storage.used)} of ${formatBytes(overview.storage.quota)}` : '',
    },
    ...otherSources.map((s) => ({ key: `src:${s.id}`, label: s.name, provider: s.provider, sub: s.status !== 'ok' ? statusLabel(s.status) : s.access_model === 'personal_accounts' ? 'Your own account' : PROVIDERS[s.provider] ? PROVIDERS[s.provider].label : '', warn: s.status !== 'ok' || s.needs_personal_connection })),
    { key: 'attachments', label: 'Chat attachments', icon: '💬', sub: 'Files posted in channels you can read' },
    ...(perms.upload ? [{ key: 'trash', label: 'Trash', icon: '🗑️', sub: 'Restore deleted files' }] : []),
    { key: 'storage', label: 'Storage & connections', icon: '📊', sub: '' },
  ];
  const selKey = sel.kind === 'source' ? `src:${sel.id}` : sel.kind;

  return (
    <div className="coms-view vxo-view vx-pz" data-testid="library-view">
      <header className="vxo-head">
        <div className="vxo-head-title">
          <h1>Files &amp; resources</h1>
          <span className="vxo-head-sub">{server && server.name}</span>
        </div>
        <div className="vxo-tabs" role="tablist" aria-label="Library sections">
          {[
            ['files', 'Files'],
            ['resources', 'Resources'],
          ].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={`vxo-tab${tab === id ? ' is-active' : ''}`} onClick={() => setTab(id)} data-testid={`library-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>
      </header>
      <ErrorNote error={err} onRetry={load} />
      {tab === 'resources' ? (
        <ResourcesPanel server={server} initial={initial} />
      ) : (
        <div className={`vxo-split${mobileNav ? ' is-nav-open' : ''}`}>
          <nav className="vxo-nav" aria-label="Libraries">
            {navItems.map((n) => (
              <button
                key={n.key}
                type="button"
                className={`vxo-nav-item${selKey === n.key ? ' is-active' : ''}${n.warn ? ' is-warn' : ''}`}
                aria-current={selKey === n.key ? 'page' : undefined}
                onClick={() => {
                  setSel(n.key.startsWith('src:') ? { kind: 'source', id: Number(n.key.slice(4)) } : { kind: n.key });
                  setMobileNav(false);
                }}
                data-testid={`library-nav-${n.key}`}
              >
                <span className="vxo-nav-icon" aria-hidden="true">
                  {n.provider ? <ProviderBadge provider={n.provider} /> : n.icon}
                </span>
                <span className="vxo-nav-text">
                  <span className="vxo-nav-label">{n.label}</span>
                  {n.sub ? <span className="vxo-nav-sub">{n.sub}</span> : null}
                </span>
              </button>
            ))}
            {overview ? (
              <button type="button" className="vx-btn vxo-connect-btn" onClick={() => setConnectOpen(true)} data-testid="connect-storage">
                {perms.manage_storage ? '＋ Connect storage' : '☁️ Your connected accounts'}
              </button>
            ) : null}
          </nav>
          <section className="vxo-main">
            <button type="button" className="vx-btn small vxo-mobile-nav-btn" onClick={() => setMobileNav((v) => !v)} aria-expanded={mobileNav}>
              ☰ Libraries
            </button>
            {!overview && !err ? <Spinner /> : null}
            {overview && sel.kind === 'internal' ? <FilesBrowser base={base} serverKey={serverKey} overview={overview} settings={filesSettings} onChanged={load} onSettingsChanged={loadSettings} initialFile={initial && initial.file_id} /> : null}
            {overview && sel.kind === 'attachments' ? <AttachmentsBrowser base={base} server={server} canAdd={perms.upload} /> : null}
            {overview && sel.kind === 'trash' ? <TrashBrowser base={base} onChanged={load} /> : null}
            {overview && sel.kind === 'storage' ? <StorageDashboard base={base} overview={overview} sources={sources} perms={perms} filesSettings={filesSettings} onConnect={() => setConnectOpen(true)} onChanged={load} /> : null}
            {overview && sel.kind === 'source'
              ? (() => {
                  const s = sources.find((x) => x.id === sel.id);
                  return s ? <ExternalBrowser key={s.id} base={base} source={s} serverKey={serverKey} onChanged={load} initialItem={sel.ext_id} canShareResource={!!perms.manage_resources} /> : <EmptyState title="Library not found">It may have been disconnected.</EmptyState>;
                })()
              : null}
          </section>
        </div>
      )}
      {connectOpen ? (
        <ConnectStorageDialog
          base={base}
          serverKey={serverKey}
          canManage={!!perms.manage_storage}
          onClose={() => {
            setConnectOpen(false);
            load();
          }}
          onConnected={(src) => {
            setConnectOpen(false);
            load().then(() => src && setSel({ kind: 'source', id: src.id }));
          }}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ Files: one list for every storage
// Voxemera's own files and Google Drive links share the same folders, search, sorting and actions. New files go
// where the server's setting and the member's own Drive allow: straight into the member's Google Drive (the
// browser sends the bytes to Google; Voxemera keeps a link) or into Voxemera's small built-in storage.
const LINK_VISIBILITY_OPTIONS = [
  ['library', 'Everyone who can see this folder'],
  ['private', 'Only me'],
];
const TYPE_TEST = {
  image: (m) => m.startsWith('image/'),
  video: (m) => m.startsWith('video/'),
  audio: (m) => m.startsWith('audio/'),
  pdf: (m) => m === 'application/pdf',
  text: (m) => m.startsWith('text/'),
  document: (m) => m.startsWith('application/vnd.') || m === 'application/msword' || m === 'application/rtf',
  archive: (m) => /zip|rar|7z|gzip/.test(m),
};

function sortEntries(list, sort, dir) {
  const k = {
    name: (e) => String(e.name || '').toLowerCase(),
    updated: (e) => String(e.updated || ''),
    created: (e) => String(e.created || ''),
    size: (e) => Number(e.size || 0),
    type: (e) => String(e.mime || ''),
  }[sort] || ((e) => String(e.name || '').toLowerCase());
  const sign = dir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const x = k(a);
    const y = k(b);
    if (x < y) return -1 * sign;
    if (x > y) return 1 * sign;
    return String(a.name).localeCompare(String(b.name));
  });
}

function FilesBrowser({ base, serverKey, overview, settings, onChanged, onSettingsChanged, initialFile }) {
  const perms = overview.permissions;
  const [folderId, setFolderId] = useState(0);
  const [data, setData] = useState(null);
  const [links, setLinks] = useState([]);
  const [recent, setRecent] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState('name');
  const [dir, setDir] = useState('asc');
  const [view, setView] = useState(readViewMode);
  const [page, setPage] = useState(0);
  const [uploads, setUploads] = useState([]);
  const [drag, setDrag] = useState(false);
  const [details, setDetails] = useState(initialFile ? Number(initialFile) : null);
  const [moveItem, setMoveItem] = useState(null);
  const [preview, setPreview] = useState(null);
  const [linkDialog, setLinkDialog] = useState(null);
  const [shareDialog, setShareDialog] = useState(null);
  const [linkVis, setLinkVis] = useState('library');
  const [destChoice, setDestChoice] = useState(null);
  const fileInput = useRef(null);
  const [confirm, confirmEl] = useConfirm();
  const [prompt, promptEl] = usePrompt();

  const st = settings || {};
  const myDrive = st.my_drive;
  const driveUsable = !!(st.drive_available && st.drive_links && st.can_add);
  const driveUploadReady = driveUsable && !!myDrive && myDrive.status === 'ok' && myDrive.uploads_allowed;
  const nativeAllowed = perms.upload && st.upload_destination !== 'gdrive';
  const destinations = [...(driveUploadReady ? ['gdrive'] : []), ...(nativeAllowed ? ['voxemera'] : [])];
  const dest = destChoice && destinations.includes(destChoice) ? destChoice : destinations.includes(st.effective_destination) ? st.effective_destination : destinations[0] || null;

  const seq = useRef(0);
  const load = useCallback(
    async (p = 0) => {
      const mine = ++seq.current;
      setErr(null);
      try {
        const qs = new URLSearchParams({ sort, dir, page: String(p), ...(q ? { q } : {}), ...(type ? { type } : {}) });
        const [r, l] = await Promise.all([
          apiGetJson(`${base}/library/folders/${folderId}/contents?${qs}`),
          p === 0 ? apiGetJson(`${base}/files/links?${new URLSearchParams({ folder: String(folderId || 0), ...(q ? { q } : {}) })}`).catch(() => ({ items: [] })) : Promise.resolve(null),
        ]);
        if (mine !== seq.current) return;
        setData((prev) => (p > 0 && prev ? { ...r, folders: prev.folders, files: [...prev.files, ...r.files] } : r));
        if (l) setLinks(l.items || []);
        setPage(p);
      } catch (e) {
        setErr(e);
        if (e.status === 404 && folderId) setFolderId(0);
      }
    },
    [base, folderId, sort, dir, q, type]
  );

  useEffect(() => {
    const t = setTimeout(() => load(0), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    if (folderId) return;
    apiGetJson(`${base}/library/recent`).then(setRecent).catch(() => setRecent(null));
  }, [base, folderId, data]);

  useEffect(() => {
    try {
      localStorage.setItem('vox:library:view', view);
    } catch {}
  }, [view]);

  // onChanged reloads the page shell, including the Files settings (the server's Drive library is created by
  // the first Drive file and must then stop appearing as a separate library).
  const refresh = () => {
    load(0);
    onChanged && onChanged();
  };

  const entries = useMemo(() => {
    const native = (data ? data.files : []).map((f) => ({ kind: 'native', key: `f${f.id}`, name: f.name, size: f.size, mime: f.mime, updated: f.updated_at, created: f.created_at, f }));
    const test = TYPE_TEST[type];
    const drive = links.filter((l) => !test || test(String(l.mime || ''))).map((l) => ({ kind: 'drive', key: `x${l.vox_id}`, name: l.name, size: l.size, mime: l.mime, updated: l.modified_at || l.linked_at, created: l.linked_at, l }));
    return sortEntries([...native, ...drive], sort, dir);
  }, [data, links, sort, dir, type]);

  // ---- uploads ----
  function startUploads(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!dest) {
      notify(driveUsable && !myDrive ? 'Connect your Google Drive to add files here.' : 'You cannot upload files here.', { tone: 'error' });
      return;
    }
    if (dest === 'gdrive') return uploadToDrive(files);
    const tooBig = files.find((f) => f.size > overview.storage.max_file);
    if (tooBig) {
      notify(`${tooBig.name} is larger than ${formatBytes(overview.storage.max_file)}, the limit for Voxemera’s own storage.${driveUsable ? ' Upload it to Google Drive instead.' : ''}`, { tone: 'error', ms: 8000 });
      return;
    }
    for (let i = 0; i < files.length; i += 10) {
      const batch = files.slice(i, i + 10);
      const id = `${Date.now()}-${i}`;
      const job = uploadForm(`${base}/library/files`, batch, { folder_id: folderId || '' }, (p) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x))));
      setUploads((u) => [...u, { id, names: batch.map((f) => f.name), dest: 'voxemera', progress: 0, abort: job.abort, error: null }]);
      job.promise
        .then((r) => {
          setUploads((u) => u.filter((x) => x.id !== id));
          notify(`Uploaded ${r.files.length} file${r.files.length === 1 ? '' : 's'} to Voxemera`, { tone: 'success' });
          refresh();
        })
        .catch((e) => {
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: e.cancelled ? 'Cancelled' : e.message } : x)));
          setTimeout(() => setUploads((u) => u.filter((x) => x.id !== id)), e.cancelled ? 1500 : 9000);
        });
    }
  }

  // Each file: Voxemera opens Google's upload session, the browser sends the bytes to Google, Voxemera links it.
  async function uploadToDrive(files) {
    const queue = files.map((file, i) => ({ file, id: `${Date.now()}-${i}-${file.name}` }));
    setUploads((u) => [...u, ...queue.map((x) => ({ id: x.id, names: [x.file.name], dest: 'gdrive', progress: 0, error: null }))]);
    const setU = (id, patch) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    let done = 0;
    const one = async ({ file, id }) => {
      try {
        const s = await apiPostJson(`${base}/files/uploads`, { name: file.name, size: file.size, mime: file.type || 'application/octet-stream', folder_id: folderId || 0, visibility: linkVis });
        const job = directUpload(s.upload, file, (pr) => setU(id, { progress: pr }));
        setU(id, {
          abort: () => {
            job.abort();
            apiDelete(`${base}/files/uploads/${s.token}`).catch(() => {});
          },
        });
        const result = await job.promise;
        await apiPostJson(`${base}/files/uploads/${s.token}/complete`, { result });
        setUploads((u) => u.filter((x) => x.id !== id));
        done += 1;
      } catch (e) {
        if (e.data && e.data.code === 'uploads_not_allowed') onSettingsChanged && onSettingsChanged();
        setU(id, { error: e.cancelled ? 'Cancelled' : e.message });
        setTimeout(() => setUploads((u) => u.filter((x) => x.id !== id)), e.cancelled ? 1500 : 9000);
      }
    };
    // Three at a time.
    const pending = [...queue];
    await Promise.all([0, 1, 2].map(async () => {
      while (pending.length) await one(pending.shift());
    }));
    if (done) {
      notify(`Uploaded ${done} file${done === 1 ? '' : 's'} to your Google Drive`, { tone: 'success' });
      refresh();
    }
  }

  // ---- Google Drive: connect, allow uploads, pick ----
  async function connectDrive() {
    const r = await connectWithFeedback('gdrive', 'Google Drive', myDrive && myDrive.needs_reconnect ? { reconnect: myDrive.id } : { mode: 'readwrite' });
    if (r.ok) onSettingsChanged && onSettingsChanged();
  }

  async function allowUploads() {
    const ok = await confirm({
      title: 'Upload new files to your Google Drive?',
      body: `New files you add here will be uploaded to the “Voxemera” folder in your Google Drive (${myDrive.account}) and linked in this server. Google does not ask for any new permission: Voxemera can already create files there. You can switch this off again under “Your connected accounts”.`,
      confirmLabel: 'Use my Google Drive',
    });
    if (!ok) return;
    try {
      await apiPatchJson(`/me/storage/connections/${myDrive.id}`, { mode: 'readwrite' });
      setDestChoice('gdrive');
      onSettingsChanged && onSettingsChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function addFromDrive() {
    try {
      const grant = await apiPostJson(`${base}/files/picker`, {});
      const ids = await pickDriveFiles(grant, { title: 'Choose Google Drive files to add to this server' });
      if (!ids.length) return;
      const r = await apiPostJson(`${base}/files/picked`, { ids, folder_id: folderId || 0, visibility: linkVis });
      notify(`Added ${r.items.length} file${r.items.length === 1 ? '' : 's'} from Google Drive${r.refused ? ` (${r.refused} could not be opened with your account)` : ''}`, { tone: 'success' });
      refresh();
    } catch (e) {
      if (e.data && e.data.code === 'personal_connection_required') return connectDrive();
      notify(e.message, { tone: 'error' });
    }
  }

  // ---- Drive link actions (opening always goes through Google, which checks the viewer's own access) ----
  const linkUrl = (l, rest) => `${base}/library/sources/${l.source_id}/items/${l.vox_id}${rest}`;
  function openLink(l) {
    openWhenReady(apiGetJson(linkUrl(l, '/open?format=json'))).catch((e) => {
      notify(e.message, { tone: 'error' });
      if (e.status === 410 || e.status === 404) refresh();
    });
  }
  function downloadLink(l, d) {
    openWhenReady(apiGetJson(linkUrl(l, `/download?format=json&export=${encodeURIComponent(d.format)}`))).catch((e) => notify(e.message, { tone: 'error' }));
  }
  async function changeLinkVisibility(l, v) {
    try {
      await apiPatchJson(`${base}/library/sources/${l.source_id}/links/${l.vox_id}`, v);
      notify('Updated who can see this file', { tone: 'success' });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function removeLink(l) {
    const ok = await confirm({
      title: `Remove “${l.name}” from this server’s files?`,
      body: `Only the link is removed. The file stays in ${l.link.mine ? 'your' : 'its owner’s'} Google Drive exactly as it is; nothing is deleted there.`,
      confirmLabel: 'Remove from files',
    });
    if (!ok) return;
    try {
      const r = await apiDelete(linkUrl(l, ''));
      notify(r.note, { tone: 'success' });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  // ---- Voxemera folders and files ----
  async function newFolder() {
    const name = await prompt({ title: 'New folder', label: 'Folder name', confirmLabel: 'Create', maxLength: 100 });
    if (!name) return;
    try {
      await apiPostJson(`${base}/library/folders`, { name, parent_id: folderId || null });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function renameFolder(f) {
    const name = await prompt({ title: 'Rename folder', label: 'Folder name', value: f.name, maxLength: 100 });
    if (!name || name === f.name) return;
    try {
      await apiPatchJson(`${base}/library/folders/${f.id}`, { name });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function deleteFolder(f) {
    if (!(await confirm({ title: `Delete “${f.name}”?`, body: 'The folder and everything in it move to the trash. They can be restored for 30 days. Google Drive files linked in it are not deleted in Drive.', confirmLabel: 'Move to trash', danger: true }))) return;
    try {
      await apiDelete(`${base}/library/folders/${f.id}`);
      notify('Moved to trash', { tone: 'success' });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function renameFile(f) {
    const name = await prompt({ title: 'Rename file', label: 'File name', value: f.name, hint: 'The extension cannot change.', maxLength: 180 });
    if (!name || name === f.name) return;
    try {
      await apiPatchJson(`${base}/library/files/${f.id}`, { name });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function deleteFile(f) {
    if (!(await confirm({ title: `Delete “${f.name}”?`, body: 'It moves to the trash and can be restored for 30 days. Resources and bookmarks pointing at it will show it as missing.', confirmLabel: 'Move to trash', danger: true }))) return;
    try {
      await apiDelete(`${base}/library/files/${f.id}`);
      setDetails(null);
      notify('Moved to trash', { tone: 'success' });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function copyFile(f) {
    try {
      await apiPostJson(`${base}/library/files/${f.id}/copy`, { folder_id: folderId || null });
      notify('Copied', { tone: 'success' });
      refresh();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  async function restrictFolder(f) {
    const roles = await apiGetJson(`/servers/${encodeURIComponent(serverKey)}/roles`).catch(() => null);
    const list = (roles && (roles.roles || roles)) || [];
    setMoveItem({ kind: 'restrict', folder: f, roles: Array.isArray(list) ? list : [] });
  }

  const storage = overview.storage;
  const canDrop = !!dest;
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (!canDrop) return;
    startUploads(e.dataTransfer && e.dataTransfer.files);
  };
  const nothing = data && !data.folders.length && !entries.length;
  const destLabel = (d) => (d === 'gdrive' ? `Your Google Drive${myDrive && myDrive.account ? ` (${myDrive.account})` : ''}` : `Voxemera storage (files up to ${formatBytes(storage.max_file)})`);

  return (
    <div
      className={`vxo-browser${drag ? ' is-drag' : ''}`}
      onDragOver={(e) => {
        if (!canDrop) return;
        if (Array.from(e.dataTransfer.types || []).includes('Files')) {
          e.preventDefault();
          setDrag(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDrag(false);
      }}
      onDrop={onDrop}
      data-testid="internal-browser"
    >
      <div className="vxo-toolbar">
        <Breadcrumbs crumbs={[{ id: 0, name: 'Files' }, ...((data && data.breadcrumbs) || [])]} onGo={(id) => setFolderId(id)} />
        <div className="vxo-toolbar-actions">
          <input className="vxo-input" type="search" placeholder="Search this folder" aria-label="Search this folder" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="vxo-select" aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="vxo-select"
            aria-label="Sort"
            value={`${sort}:${dir}`}
            onChange={(e) => {
              const [s, d] = e.target.value.split(':');
              setSort(s);
              setDir(d);
            }}
          >
            {SORTS.flatMap(([v, l]) => [
              <option key={`${v}a`} value={`${v}:asc`}>
                {l} ↑
              </option>,
              <option key={`${v}d`} value={`${v}:desc`}>
                {l} ↓
              </option>,
            ])}
          </select>
          <div className="vx-seg vxo-seg" role="group" aria-label="View">
            <button type="button" className={`vxo-seg-btn${view === 'list' ? ' is-on' : ''}`} aria-pressed={view === 'list'} onClick={() => setView('list')} title="List view">
              ☰
            </button>
            <button type="button" className={`vxo-seg-btn${view === 'grid' ? ' is-on' : ''}`} aria-pressed={view === 'grid'} onClick={() => setView('grid')} title="Grid view">
              ▦
            </button>
          </div>
          {perms.upload ? (
            <button type="button" className="vx-btn" onClick={newFolder} data-testid="library-new-folder">
              New folder
            </button>
          ) : null}
          {st.picker ? (
            <button type="button" className="vx-btn" onClick={addFromDrive} data-testid="files-add-from-drive">
              <ProviderBadge provider="gdrive" /> Add from Google Drive
            </button>
          ) : null}
          {dest ? (
            <>
              <button type="button" className="vx-btn primary" onClick={() => fileInput.current && fileInput.current.click()} data-testid="library-upload">
                Upload
              </button>
              <input ref={fileInput} type="file" multiple hidden onChange={(e) => {
                startUploads(e.target.files);
                e.target.value = '';
              }} data-testid="library-file-input" />
            </>
          ) : null}
        </div>
      </div>
      {perms.upload && st.drive_available && st.drive_links ? (
        <div className="vxo-dest" data-testid="files-destination">
          {destinations.length > 1 ? (
            <label className="vxo-dest-label">
              <span>New files go to</span>
              <select className="vxo-select" value={dest || ''} onChange={(e) => setDestChoice(e.target.value)} data-testid="files-destination-select">
                {destinations.map((d) => (
                  <option key={d} value={d}>
                    {destLabel(d)}
                  </option>
                ))}
              </select>
            </label>
          ) : dest ? (
            <span className="vxo-dest-label">
              New files go to: <strong><ProviderBadge provider={dest} /> {destLabel(dest)}</strong>
            </span>
          ) : null}
          {dest === 'gdrive' || st.picker ? (
            <label className="vxo-dest-label">
              <span>Google Drive files are listed for</span>
              <select className="vxo-select" value={linkVis} onChange={(e) => setLinkVis(e.target.value)} data-testid="files-link-visibility">
                {LINK_VISIBILITY_OPTIONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {!myDrive ? (
            <button type="button" className="vx-btn small primary" onClick={connectDrive} data-testid="files-connect-drive">
              Connect Google Drive
            </button>
          ) : myDrive.needs_reconnect ? (
            <button type="button" className="vx-btn small primary" onClick={connectDrive}>
              Reconnect Google Drive
            </button>
          ) : !myDrive.uploads_allowed ? (
            <button type="button" className="vx-btn small" onClick={allowUploads} data-testid="files-allow-drive-uploads">
              Upload to my Google Drive instead
            </button>
          ) : null}
          <span className="vxo-muted">
            {dest === 'gdrive'
              ? 'Files stay in your Google Drive; this server keeps only a link. Google decides who can open them: share a file in Google Drive (or from its menu here) so others can.'
              : myDrive
                ? 'Google Drive files you add stay in your Drive; Google decides who can open them.'
                : 'Connect Google Drive to keep files of any size in your own Drive and link them here.'}
          </span>
        </div>
      ) : null}
      {dest !== 'gdrive' ? (
        <div className="vxo-quota" aria-label="Voxemera storage">
          <div className="vxo-quota-bar" role="progressbar" aria-valuemin={0} aria-valuemax={storage.quota} aria-valuenow={storage.used} aria-label="Voxemera storage used">
            <span style={{ width: `${Math.min(100, (storage.used / Math.max(1, storage.quota)) * 100)}%` }} className={storage.warning ? 'is-warn' : ''} />
          </div>
          <span>
            Voxemera storage: {formatBytes(storage.used)} of {formatBytes(storage.quota)} used · files up to {formatBytes(storage.max_file)} · <span className="vxo-muted">{data && data.access_note}</span>
          </span>
        </div>
      ) : null}
      {dest !== 'gdrive' && storage.warning ? <div className="vxo-warn">{storage.warning === 'full' ? 'Voxemera storage is full. Delete files or use Google Drive.' : 'Voxemera storage is nearly full.'}</div> : null}
      {uploads.length ? (
        <ul className="vxo-uploads" aria-label="Uploads" aria-live="polite">
          {uploads.map((u) => (
            <li key={u.id} className={u.error ? 'is-error' : ''}>
              <span className="vxo-upload-name">
                <ProviderBadge provider={u.dest === 'gdrive' ? 'gdrive' : 'voxemera'} /> {u.names.join(', ')}
              </span>
              {u.error ? <span className="vxo-upload-err">{u.error}</span> : <progress max={1} value={u.progress} aria-label={`Upload progress for ${u.names.join(', ')}`} />}
              {!u.error && u.abort ? (
                <button type="button" className="vx-btn small" onClick={u.abort}>
                  Cancel
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <ErrorNote error={err} onRetry={() => load(0)} />
      {!folderId && recent && !q && !type && (recent.uploaded.length || recent.accessed.length) ? (
        <div className="vxo-recent">
          {recent.accessed.length ? <RecentStrip title="Recently opened" items={recent.accessed} onOpen={(f) => setDetails(f.id)} /> : null}
          {recent.uploaded.length ? <RecentStrip title="Recently uploaded" items={recent.uploaded} onOpen={(f) => setDetails(f.id)} /> : null}
        </div>
      ) : null}
      {!data && !err ? <Spinner /> : null}
      {nothing ? (
        <EmptyState icon="📂" title={q || type ? 'Nothing matches' : 'This folder is empty'}>
          {dest ? `Drop files here or use Upload${st.picker ? ', or add files from Google Drive' : ''}.` : 'Nothing has been shared here yet.'}
        </EmptyState>
      ) : null}
      {data ? (
        <div className={`vxo-items is-${view}`} role="list" data-testid="files-list">
          {data.folders.map((f) => (
            <ItemRow
              key={`d${f.id}`}
              view={view}
              icon="📁"
              name={f.name}
              meta={f.restricted_role_ids.length ? 'Restricted folder' : 'Folder'}
              onOpen={() => setFolderId(f.id)}
              actions={[
                f.can_edit && { label: 'Rename', onClick: () => renameFolder(f) },
                f.can_edit && { label: 'Move…', onClick: () => setMoveItem({ kind: 'folder', item: f }) },
                perms.manage && { label: 'Restrict to roles…', onClick: () => restrictFolder(f) },
                f.can_edit && { label: 'Delete', danger: true, onClick: () => deleteFolder(f) },
              ]}
            />
          ))}
          {entries.map((e) => {
            if (e.kind === 'drive') {
              const l = e.l;
              const gone = l.link.unavailable;
              return (
                <ItemRow
                  key={e.key}
                  view={view}
                  testId={`files-drive-${l.vox_id}`}
                  icon={kindIcon(l.mime, { folder: l.is_folder })}
                  provider="gdrive"
                  name={l.name}
                  badge={gone ? 'Unavailable' : l.link.visibility === 'private' ? 'Only you' : l.link.visibility === 'roles' ? 'Some roles' : l.link.shared_by_link ? 'Anyone with the link' : null}
                  meta={
                    gone
                      ? 'No longer available in Google Drive (deleted, trashed or unshared there)'
                      : [l.is_folder ? 'Drive folder' : l.size != null ? formatBytes(l.size) : 'Google file', `Google Drive · ${l.link.mine ? 'you' : l.link.linked_by || 'a member'}`, e.updated ? formatDate(e.updated) : null].filter(Boolean).join(' · ')
                  }
                  onOpen={() => (gone ? null : l.preview_url ? setPreview(l) : openLink(l))}
                  actions={[
                    !gone && l.preview_url && { label: 'Preview', onClick: () => setPreview(l) },
                    !gone && { label: 'Open in Google Drive', onClick: () => openLink(l) },
                    ...(!gone ? (l.downloads || []).map((d) => ({ label: d.label, onClick: () => downloadLink(l, d) })) : []),
                    { label: 'Save bookmark', onClick: () => saveBookmark('external', l.vox_id) },
                    l.link.can_change && { label: 'Who can see this…', onClick: () => setLinkDialog(l) },
                    l.link.can_change && !gone && { label: 'Sharing in Google Drive…', onClick: () => setShareDialog(l) },
                    l.link.can_move && { label: 'Move…', onClick: () => setMoveItem({ kind: 'link', item: l }) },
                    l.link.can_remove && { label: 'Remove from files', onClick: () => removeLink(l) },
                  ]}
                />
              );
            }
            const f = e.f;
            return (
              <ItemRow
                key={e.key}
                view={view}
                icon={kindIcon(f.mime)}
                provider="voxemera"
                name={f.name}
                thumb={f.mime && f.mime.startsWith('image/') && view === 'grid' ? `/api/coms${base}/library/files/${f.id}/download?inline=1` : null}
                meta={`${formatBytes(f.size)} · Voxemera · ${f.owner_name || 'unknown'} · ${formatDate(f.updated_at)}${f.version_count > 1 ? ` · v${f.version_count}` : ''}`}
                onOpen={() => setDetails(f.id)}
                actions={[
                  { label: 'Preview & versions', onClick: () => setDetails(f.id) },
                  { label: 'Download', href: `/api/coms${base}/library/files/${f.id}/download` },
                  { label: 'Save bookmark', onClick: () => saveBookmark('file', f.id) },
                  f.can_edit && { label: 'Rename', onClick: () => renameFile(f) },
                  f.can_edit && { label: 'Move…', onClick: () => setMoveItem({ kind: 'file', item: f }) },
                  perms.upload && { label: 'Make a copy', onClick: () => copyFile(f) },
                  f.can_edit && { label: 'Delete', danger: true, onClick: () => deleteFile(f) },
                ]}
              />
            );
          })}
        </div>
      ) : null}
      {data && data.has_more ? (
        <button type="button" className="vx-btn vxo-more" onClick={() => load(page + 1)}>
          Load more ({data.total - data.files.length} left)
        </button>
      ) : null}
      {drag ? <div className="vxo-dropzone" aria-hidden="true">Drop to upload to {dest === 'gdrive' ? 'your Google Drive' : 'Voxemera'} ({data && data.folder ? data.folder.name : 'Files'})</div> : null}
      {details ? <FileDetails base={base} fileId={details} onClose={() => setDetails(null)} onChanged={refresh} onDelete={deleteFile} onRename={renameFile} /> : null}
      {preview ? <DrivePreview link={preview} onClose={() => setPreview(null)} onOpen={() => openLink(preview)} onDownload={(d) => downloadLink(preview, d)} /> : null}
      {linkDialog ? (
        <LinkVisibilityDialog
          base={base}
          title={`Who can see “${linkDialog.name}”?`}
          confirmLabel="Save"
          initial={{ visibility: linkDialog.link.visibility, role_ids: linkDialog.link.role_ids || [] }}
          onClose={() => setLinkDialog(null)}
          onConfirm={(v) => {
            const l = linkDialog;
            setLinkDialog(null);
            changeLinkVisibility(l, v);
          }}
        />
      ) : null}
      {shareDialog ? (
        <DriveSharingDialog
          base={base}
          source={{ id: shareDialog.source_id }}
          item={shareDialog}
          onClose={() => setShareDialog(null)}
          onChanged={() => {
            setShareDialog(null);
            refresh();
          }}
        />
      ) : null}
      {moveItem && moveItem.kind !== 'restrict' ? (
        <FolderPicker
          base={base}
          title={`Move “${moveItem.item.name}”`}
          excludeId={moveItem.kind === 'folder' ? moveItem.item.id : null}
          onClose={() => setMoveItem(null)}
          onPick={async (target) => {
            try {
              if (moveItem.kind === 'folder') await apiPatchJson(`${base}/library/folders/${moveItem.item.id}`, { parent_id: target || null });
              else if (moveItem.kind === 'link') await apiPatchJson(`${base}/files/links/${moveItem.item.vox_id}`, { folder_id: target || 0 });
              else await apiPatchJson(`${base}/library/files/${moveItem.item.id}`, { folder_id: target || null });
              setMoveItem(null);
              notify('Moved', { tone: 'success' });
              refresh();
            } catch (e) {
              notify(e.message, { tone: 'error' });
            }
          }}
        />
      ) : null}
      {moveItem && moveItem.kind === 'restrict' ? <RestrictDialog base={base} folder={moveItem.folder} roles={moveItem.roles} onClose={() => setMoveItem(null)} onSaved={refresh} /> : null}
      {confirmEl}
      {promptEl}
    </div>
  );
}

// Google's own preview of a Drive file (shown inside Google's frame, so Google checks the viewer's access).
function DrivePreview({ link, onClose, onOpen, onDownload }) {
  return (
    <Dialog title={link.name} onClose={onClose} wide>
      <iframe className="vxo-drive-preview" src={link.preview_url} title={`Preview of ${link.name}`} allow="autoplay" referrerPolicy="no-referrer" />
      <p className="vxo-hint">Shown by Google Drive. If you see “You need access”, the owner has not shared this file with your Google account.</p>
      <div className="vxo-actions">
        <button type="button" className="vx-btn primary" onClick={onOpen}>
          Open in Google Drive
        </button>
        {(link.downloads || []).map((d) => (
          <button key={d.format} type="button" className="vx-btn" onClick={() => onDownload(d)}>
            {d.label}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

function RecentStrip({ title, items, onOpen }) {
  return (
    <div className="vxo-recent-row">
      <div className="vxo-recent-title">{title}</div>
      <div className="vxo-recent-items">
        {items.slice(0, 8).map((f) => (
          <button key={f.id} type="button" className="vxo-chip" onClick={() => onOpen(f)} title={f.name}>
            <span aria-hidden="true">{kindIcon(f.mime)}</span>
            <span className="vxo-chip-text">{f.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FileDetails({ base, fileId, onClose, onChanged, onDelete, onRename }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const verInput = useRef(null);
  const [confirm, confirmEl] = useConfirm();
  const load = useCallback(() => {
    apiGetJson(`${base}/library/files/${fileId}`).then(setD, setErr);
  }, [base, fileId]);
  useEffect(() => {
    load();
  }, [load]);
  const f = d && d.file;
  async function uploadVersion(file) {
    if (!file) return;
    setBusy(true);
    try {
      await uploadForm(`${base}/library/files/${fileId}/versions`, [{ field: 'files', file }], {}).promise;
      notify('New version uploaded', { tone: 'success' });
      load();
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }
  async function restore(v) {
    if (!(await confirm({ title: `Restore version ${v.version_no}?`, body: 'It becomes the newest version. Nothing is deleted: the current version stays in the history.', confirmLabel: 'Restore' }))) return;
    try {
      await apiPostJson(`${base}/library/files/${fileId}/versions/${v.id}/restore`, {});
      notify('Version restored', { tone: 'success' });
      load();
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  return (
    <Dialog title={f ? f.name : 'File'} onClose={onClose} wide>
      <ErrorNote error={err} />
      {!d && !err ? <Spinner /> : null}
      {f ? (
        <div className="vxo-details">
          <div className="vxo-details-preview">
            <FilePreview key={f.current_version_id} base={base} file={f} metadata={d.metadata} />
          </div>
          <div className="vxo-details-side">
            <dl className="vxo-dl">
              <dt>Where</dt>
              <dd>
                <ProviderBadge provider="voxemera" withLabel /> {d.breadcrumbs.length ? `· ${['Files', ...d.breadcrumbs.map((c) => c.name)].join(' › ')}` : '· Files'}
              </dd>
              <dt>Type</dt>
              <dd>{f.mime}</dd>
              <dt>Size</dt>
              <dd>{formatBytes(f.size)}</dd>
              <dt>Owner</dt>
              <dd>{f.owner_name || '—'}</dd>
              <dt>Uploaded</dt>
              <dd>{formatDate(f.created_at)}</dd>
              <dt>Changed</dt>
              <dd>{formatDate(f.updated_at)}</dd>
              <dt>Access</dt>
              <dd>{d.access_note}</dd>
            </dl>
            <div className="vxo-actions">
              <a className="vx-btn primary" href={`/api/coms${base}/library/files/${f.id}/download`}>
                Download
              </a>
              <button type="button" className="vx-btn" onClick={() => saveBookmark('file', f.id)}>
                🔖 Save bookmark
              </button>
              {f.can_edit ? (
                <>
                  <button type="button" className="vx-btn" onClick={() => onRename(f).then(load)}>
                    Rename
                  </button>
                  <button type="button" className="vx-btn" disabled={busy} onClick={() => verInput.current && verInput.current.click()}>
                    Upload new version
                  </button>
                  <input ref={verInput} type="file" hidden onChange={(e) => {
                    uploadVersion(e.target.files[0]);
                    e.target.value = '';
                  }} />
                  <button type="button" className="vx-btn danger" onClick={() => onDelete(f)}>
                    Delete
                  </button>
                </>
              ) : null}
            </div>
            <h3 className="vxo-h3">Version history</h3>
            <ol className="vxo-versions">
              {d.versions.map((v) => (
                <li key={v.id} className={v.is_current ? 'is-current' : ''}>
                  <div>
                    <strong>v{v.version_no}</strong> {v.is_current ? <span className="vxo-badge">current</span> : null}
                    <div className="vxo-muted">
                      {formatDate(v.created_at)} · {v.uploaded_by_name || 'unknown'} · {formatBytes(v.size)}
                      {v.note ? ` · ${v.note}` : ''}
                    </div>
                  </div>
                  <div className="vxo-version-actions">
                    <a className="vx-btn small" href={`/api/coms${base}/library/files/${f.id}/download?version=${v.id}`}>
                      Download
                    </a>
                    {!v.is_current && f.can_edit ? (
                      <button type="button" className="vx-btn small" onClick={() => restore(v)}>
                        Restore
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
      {confirmEl}
    </Dialog>
  );
}

function FolderPicker({ base, title, excludeId, onClose, onPick }) {
  const [stack, setStack] = useState([{ id: 0, name: 'Files' }]);
  const [folders, setFolders] = useState(null);
  const cur = stack[stack.length - 1];
  useEffect(() => {
    setFolders(null);
    apiGetJson(`${base}/library/folders/${cur.id}/contents?page=0`).then((r) => setFolders(r.folders.filter((f) => f.id !== excludeId)), () => setFolders([]));
  }, [base, cur.id, excludeId]);
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" onClick={() => onPick(cur.id)}>
            Move to {cur.name}
          </button>
        </>
      }
    >
      <Breadcrumbs crumbs={stack} onGo={(_id, i) => setStack(stack.slice(0, i + 1))} />
      {!folders ? <Spinner /> : null}
      {folders && !folders.length ? <p className="vxo-muted">No subfolders.</p> : null}
      <ul className="vxo-pick">
        {(folders || []).map((f) => (
          <li key={f.id}>
            <button type="button" className="vxo-linkbtn" onClick={() => setStack([...stack, { id: f.id, name: f.name }])}>
              📁 {f.name}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

function RestrictDialog({ base, folder, roles, onClose, onSaved }) {
  const [ids, setIds] = useState(folder.restricted_role_ids || []);
  const assignable = roles.filter((r) => !Number(r.is_default));
  return (
    <Dialog
      title={`Who can see “${folder.name}”?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            onClick={async () => {
              try {
                await apiPatchJson(`${base}/library/folders/${folder.id}`, { restricted_role_ids: ids });
                notify(ids.length ? 'Folder restricted' : 'Folder visible to everyone with library access', { tone: 'success' });
                onClose();
                onSaved();
              } catch (e) {
                notify(e.message, { tone: 'error' });
              }
            }}
          >
            Save
          </button>
        </>
      }
    >
      <p className="vxo-hint">With no roles selected, everyone with library access sees this folder. Selected roles (and library managers) only, otherwise. Subfolders inherit the restriction.</p>
      <div className="vxo-checks">
        {assignable.map((r) => (
          <label key={r.id} className="vxo-check">
            <input type="checkbox" checked={ids.includes(Number(r.id))} onChange={(e) => setIds(e.target.checked ? [...ids, Number(r.id)] : ids.filter((x) => x !== Number(r.id)))} />
            <span>{r.name}</span>
          </label>
        ))}
        {!assignable.length ? <p className="vxo-muted">This server has no roles yet.</p> : null}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------ chat attachments
function AttachmentsBrowser({ base, server, canAdd }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [channel, setChannel] = useState('');
  const [page, setPage] = useState(0);
  const [confirm, confirmEl] = useConfirm();
  const textChannels = useMemo(() => ((server && server.channels) || []).filter((c) => c.kind === 'text'), [server]);
  const seq = useRef(0);
  const load = useCallback(
    async (p = 0) => {
      const mine = ++seq.current;
      try {
        const qs = new URLSearchParams({ page: String(p), ...(type ? { type } : {}), ...(q ? { q } : {}), ...(channel ? { channel } : {}) });
        const r = await apiGetJson(`${base}/library/attachments?${qs}`);
        if (mine !== seq.current) return;
        setData((prev) => (p && prev ? { ...r, attachments: [...prev.attachments, ...r.attachments] } : r));
        setPage(p);
        setErr(null);
      } catch (e) {
        setErr(e);
      }
    },
    [base, type, q, channel]
  );
  useEffect(() => {
    const t = setTimeout(() => load(0), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  async function add(a, confirmBroaden = false) {
    try {
      await apiPostJson(`${base}/library/attachments/${a.id}/add`, { confirm_broaden: confirmBroaden });
      notify(`Added ${a.name} to Voxemera files`, { tone: 'success' });
    } catch (e) {
      if (e.data && e.data.code === 'confirm_broaden') {
        if (await confirm({ title: 'Share a private-channel file?', body: e.message, confirmLabel: 'Add to library', danger: true })) add(a, true);
        return;
      }
      notify(e.message, { tone: 'error' });
    }
  }

  return (
    <div className="vxo-browser" data-testid="attachments-browser">
      <div className="vxo-toolbar">
        <div className="vxo-toolbar-title">
          <h2>Chat attachments</h2>
          <span className="vxo-muted">Only files from channels you can read are listed. They stay where they were posted.</span>
        </div>
        <div className="vxo-toolbar-actions">
          <input className="vxo-input" type="search" placeholder="Search names" aria-label="Search attachments" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="vxo-select" aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="">All channels</option>
            {textChannels.map((c) => (
              <option key={c.id} value={c.db_id != null ? c.db_id : c.id}>
                #{c.name}
              </option>
            ))}
          </select>
          <select className="vxo-select" aria-label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            <option value="image">Images</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="file">Archives</option>
          </select>
        </div>
      </div>
      <ErrorNote error={err} onRetry={() => load(0)} />
      {!data && !err ? <Spinner /> : null}
      {data && !data.attachments.length ? <EmptyState icon="💬" title="No attachments found">Files posted in chat appear here automatically (chat attachments expire after a while; add important ones to the library).</EmptyState> : null}
      {data ? (
        <div className="vxo-items is-list" role="list">
          {data.attachments.map((a) => (
            <ItemRow
              key={a.id}
              icon={kindIcon(a.mime, { kind: a.kind })}
              provider="voxemera"
              name={a.name}
              badge={a.channel_private ? 'private channel' : null}
              meta={`${formatBytes(a.size)} · ${a.owner_name || 'unknown'} · #${a.channel_name} · ${formatDate(a.created_at)}${a.expires_at ? ` · expires ${formatDate(a.expires_at, { time: false })}` : ''}`}
              onOpen={() => openTarget({ kind: 'message', server_key: String(server.id), channel_id: a.channel_id, message_id: a.message_id })}
              actions={[
                { label: 'Show in chat', onClick: () => openTarget({ kind: 'message', server_key: String(server.id), channel_id: a.channel_id, message_id: a.message_id }) },
                { label: 'Download', href: `/api/coms/library/attachments/${a.id}/download` },
                { label: 'Save bookmark', onClick: () => saveBookmark('attachment', a.id) },
                canAdd && { label: 'Add to Voxemera files…', onClick: () => add(a) },
              ]}
            />
          ))}
        </div>
      ) : null}
      {data && data.has_more ? (
        <button type="button" className="vx-btn vxo-more" onClick={() => load(page + 1)}>
          Load more
        </button>
      ) : null}
      {confirmEl}
    </div>
  );
}

// ------------------------------------------------------------------ trash
function TrashBrowser({ base, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => apiGetJson(`${base}/library/trash`).then(setData, setErr), [base]);
  useEffect(() => {
    load();
  }, [load]);
  async function restore(kind, id) {
    try {
      const r = await apiPostJson(`${base}/library/${kind}/${id}/restore`, {});
      notify(`Restored${r.name ? ` as “${r.name}”` : ''}`, { tone: 'success' });
      load();
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  return (
    <div className="vxo-browser" data-testid="trash-browser">
      <div className="vxo-toolbar">
        <div className="vxo-toolbar-title">
          <h2>Trash</h2>
          <span className="vxo-muted">Deleted files and folders are kept for {data ? data.purge_after_days : 30} days, then removed for good.</span>
        </div>
      </div>
      <ErrorNote error={err} onRetry={load} />
      {!data && !err ? <Spinner /> : null}
      {data && !data.files.length && !data.folders.length ? <EmptyState icon="🗑️" title="The trash is empty" /> : null}
      {data ? (
        <div className="vxo-items is-list" role="list">
          {data.folders.map((f) => (
            <ItemRow key={`d${f.id}`} icon="📁" name={f.name} meta={`Deleted ${formatDate(f.deleted_at)}`} onOpen={() => restore('folders', f.id)} actions={[{ label: 'Restore', onClick: () => restore('folders', f.id) }]} />
          ))}
          {data.files.map((f) => (
            <ItemRow key={`f${f.id}`} icon={kindIcon(f.mime)} name={f.name} meta={`${formatBytes(f.size)} · deleted ${formatDate(f.deleted_at)}`} onOpen={() => restore('files', f.id)} actions={[{ label: 'Restore', onClick: () => restore('files', f.id) }]} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ storage dashboard
// Where this server's new files go, and whether members may add Google Drive files (storage managers).
function FilesDestinationCard({ base, settings, onChanged }) {
  const [busy, setBusy] = useState(false);
  async function save(patch) {
    setBusy(true);
    try {
      await apiPut(`${base}/files/settings`, patch);
      notify('Saved', { tone: 'success' });
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="vxo-card" data-testid="files-destination-settings">
      <h3 className="vxo-h3">Where new files go</h3>
      <p className="vxo-muted">
        Google Drive keeps files in each member’s own Drive (any size); this server keeps only links, and Google’s sharing decides who can open them. Voxemera’s own storage holds small files only. Connecting a Google account never shares the rest of that Drive with the server.
      </p>
      <fieldset className="vxo-fieldset" disabled={busy}>
        <legend>Uploads in Files</legend>
        {[
          ['auto', 'Google Drive when the member has connected it, otherwise Voxemera (recommended)'],
          ['gdrive', 'Google Drive only (members without Google Drive cannot upload)'],
          ['voxemera', 'Voxemera storage only'],
        ].map(([v, label]) => (
          <label key={v} className="vxo-radio">
            <input type="radio" name="upload-destination" checked={settings.upload_destination === v} disabled={v !== 'voxemera' && !settings.drive_available} onChange={() => save({ upload_destination: v })} data-testid={`files-dest-${v}`} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      <label className="vxo-check">
        <input type="checkbox" checked={!!settings.drive_links} disabled={busy || !settings.drive_available} onChange={(e) => save({ drive_links: e.target.checked })} data-testid="files-drive-links-toggle" />
        <span>Members who may upload files can add files from their own Google Drive</span>
      </label>
      {!settings.drive_available ? <p className="vxo-muted">Google Drive is not available on this Voxemera site yet.</p> : null}
    </section>
  );
}

function StorageDashboard({ base, overview, sources, perms, filesSettings, onConnect, onChanged }) {
  const s = overview.storage;
  const [health, setHealth] = useState({});
  const [conns, setConns] = useState(null);
  const [confirm, confirmEl] = useConfirm();
  useEffect(() => {
    apiGetJson('/me/storage/connections').then((r) => setConns(r.connections), () => setConns([]));
  }, []);
  async function check(src) {
    setHealth((h) => ({ ...h, [src.id]: { loading: true } }));
    const r = await apiGetJson(`${base}/library/sources/${src.id}/status`).catch((e) => ({ status: 'error', message: e.message }));
    setHealth((h) => ({ ...h, [src.id]: r }));
  }
  async function sync(src) {
    try {
      const r = await apiPostJson(`${base}/library/sources/${src.id}/sync`, {});
      notify(`Synced ${r.count} items${r.truncated ? ' (first 5,000)' : ''}`, { tone: 'success' });
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function disconnect(src) {
    if (!(await confirm({ title: `Disconnect “${src.name}”?`, body: 'Voxemera forgets the stored credentials and cached file names. Nothing is deleted from the provider. Bookmarks and resources pointing at these files will show them as disconnected.', confirmLabel: 'Disconnect', danger: true }))) return;
    try {
      await apiDelete(`${base}/library/sources/${src.id}`);
      notify('Disconnected. No provider files were deleted.', { tone: 'success' });
      onChanged();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function revokeMine(c) {
    if (!(await confirm({ title: `Disconnect your ${c.provider_label} account?`, body: 'Libraries that use your own account will ask you to connect again. Your files stay in your account.', confirmLabel: 'Disconnect', danger: true }))) return;
    try {
      await disconnectAccount(c);
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
    setConns((list) => list.filter((x) => x.id !== c.id));
  }
  async function reconnectMine(c) {
    const r = await connectWithFeedback(c.provider, c.provider_label, { reconnect: c.id });
    if (r.ok) apiGetJson('/me/storage/connections').then((x) => setConns(x.connections), () => {});
  }
  return (
    <div className="vxo-browser vxo-dashboard" data-testid="storage-dashboard">
      <div className="vxo-toolbar">
        <div className="vxo-toolbar-title">
          <h2>Storage &amp; connections</h2>
          <span className="vxo-muted">Voxemera organises files; your chosen storage provider stores them.</span>
        </div>
        {perms.manage_storage ? (
          <button type="button" className="vx-btn primary" onClick={onConnect}>
            ＋ Connect storage
          </button>
        ) : null}
      </div>
      {perms.manage_storage && filesSettings ? <FilesDestinationCard base={base} settings={filesSettings} onChanged={onChanged} /> : null}
      <section className="vxo-card">
        <h3 className="vxo-h3">
          <ProviderBadge provider="voxemera" withLabel /> storage (small files)
        </h3>
        <div className="vxo-quota-bar big" role="progressbar" aria-valuemin={0} aria-valuemax={s.quota} aria-valuenow={s.used} aria-label="Internal storage used">
          <span style={{ width: `${Math.min(100, (s.used / Math.max(1, s.quota)) * 100)}%` }} className={s.warning ? 'is-warn' : ''} />
        </div>
        <p>
          {formatBytes(s.used)} used of {formatBytes(s.quota)} · {formatBytes(s.remaining)} left · {s.file_count} files · files up to {formatBytes(s.max_file)}
        </p>
        <p className="vxo-muted">Every version counts. Deleted files count until they leave the trash. Limits are set by the Voxemera server administrator.</p>
        {s.largest.length ? (
          <>
            <h4 className="vxo-h4">Largest files</h4>
            <ul className="vxo-plain">
              {s.largest.map((f) => (
                <li key={f.id}>
                  {f.name} — {formatBytes(f.size)}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
      {sources.map((src) => {
        const h = health[src.id];
        return (
          <section key={src.id} className="vxo-card">
            <h3 className="vxo-h3">
              <ProviderBadge provider={src.provider} withLabel /> {src.name}
            </h3>
            <p className={src.status === 'ok' ? '' : 'vxo-warn-text'}>
              {statusLabel(src.status)}
              {src.status_detail ? ` — ${src.status_detail}` : ''}
            </p>
            <p className="vxo-muted">{src.where}</p>
            <p className="vxo-muted">{src.access_note}</p>
            <ul className="vxo-plain">
              <li>Access: {src.mode === 'readwrite' ? 'read and write' : 'read-only'}</li>
              {src.root_label ? <li>Folder: {src.root_label}</li> : null}
              {src.repo ? <li>Repository: {src.repo}</li> : null}
              {src.bucket ? <li>Bucket: {src.bucket}</li> : null}
              {src.access_model === 'shared_connection' ? <li>Indexed items: {src.indexed_items}</li> : <li>Not indexed (each member browses with their own account)</li>}
              {src.last_sync_at ? <li>Last metadata sync: {formatDate(src.last_sync_at)}</li> : null}
              {h && h.account ? <li>Account: {h.account}</li> : null}
              {h && h.quota && h.quota.total ? (
                <li>
                  Provider usage: {formatBytes(h.quota.used)} of {formatBytes(h.quota.total)}
                </li>
              ) : null}
              {h && h.message ? <li className="vxo-warn-text">{h.message}</li> : null}
            </ul>
            <div className="vxo-actions">
              <button type="button" className="vx-btn small" onClick={() => check(src)} disabled={h && h.loading}>
                {h && h.loading ? 'Checking…' : 'Check connection'}
              </button>
              {src.access_model === 'shared_connection' && (perms.manage_storage || perms.manage) ? (
                <button type="button" className="vx-btn small" onClick={() => sync(src)}>
                  Sync metadata now
                </button>
              ) : null}
              {src.can_manage ? (
                <button type="button" className="vx-btn small danger" onClick={() => disconnect(src)}>
                  Disconnect
                </button>
              ) : null}
            </div>
          </section>
        );
      })}
      <section className="vxo-card">
        <h3 className="vxo-h3">Your connected accounts (personal)</h3>
        <p className="vxo-muted">Accounts you connected yourself. They stay private to you: other members never use them, and the server only sees the individual files you add to Files.</p>
        {conns && !conns.length ? <p className="vxo-muted">None yet.</p> : null}
        <div className="vxo-account-list">
          {(conns || []).map((c) => (
            <AccountCard key={c.id} conn={c} compact onReconnect={reconnectMine} onDisconnect={revokeMine} />
          ))}
        </div>
        <div className="vxo-actions">
          <button type="button" className="vx-btn small" onClick={onConnect} data-testid="manage-accounts">
            Manage accounts
          </button>
        </div>
      </section>
      {confirmEl}
    </div>
  );
}
